import React, {
  memo,
  useState,
  useEffect,
  useCallback,
  useContext,
} from "react";
import markitIcon from "../../src/assets/MarkitLogoBlue.png";
import Footer from "../components/Footer";
import TopHeader from "../components/TopHeader";
import { Colors } from "../utils/colors";
import { DotWave } from "@uiball/loaders";
import { useDispatch, useSelector } from "react-redux";
import { getAccountSlice, logoutAllUsers } from "../redux/slices/accountSlice";
import CreatorModeTopHeader from "../components/CreatorDashboard/CreatorModeTopHeader";
import { isDesktop } from "react-device-detect";
import { useOnMount } from "../hooks/useOnMount";
import RectangleButton from "../components/Buttons/RectangleButton";
import { NavigationId } from "../navigation/AppParamList";
import { useNavigate } from "react-router-dom";
import { MixpanelContext } from "../context/AnalyticsService";
import AlertContainer from "../components/Containers/AlertContainer";
import { API } from "../API";
import { isAndroidBrowser } from "../utils/navigationUtils";

// Client environment diagnostics for the loading-performance monitoring.
// Note: navigator.connection is unavailable on iOS Safari/WebKit, so `network`
// is often "unavailable" on the exact devices we're debugging — the in-app
// browser and user agent are the reliable signals there.
const getClientDiagnostics = () => {
  const nav = navigator as any;
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
  const network = conn
    ? `${conn.effectiveType ?? "?"}, rtt=${conn.rtt ?? "?"}ms, down=${
        conn.downlink ?? "?"
      }Mbps${conn.saveData ? ", saveData" : ""}`
    : "unavailable";
  const ua = navigator.userAgent;
  const inAppBrowser = /Instagram/i.test(ua)
    ? "Instagram"
    : /FBAN|FBAV/i.test(ua)
      ? "Facebook"
      : /Snapchat/i.test(ua)
        ? "Snapchat"
        : /TikTok|musical_ly|BytedanceWebview/i.test(ua)
          ? "TikTok"
          : "standalone";
  return { network, inAppBrowser, ua };
};

const LoadingScreen = memo(function LoadingFn({
  error,
  underMaintenance,
  isLoadingPage,
  loadingEventId,
  loadingPhase,
  loadingStartTime,
  getDiagnostics,
}: {
  error?: boolean;
  underMaintenance?: boolean;
  isLoadingPage?: boolean;
  loadingEventId?: string;
  loadingPhase?: string;
  loadingStartTime?: number;
  getDiagnostics?: () => string;
}) {
  const { uid, inCreatorMode, phoneNumber } =
    useSelector(getAccountSlice).accountData;
  const navigate = useNavigate();
  const mixpanel = useContext(MixpanelContext);
  const dispatch = useDispatch();
  const [showLoadingDots, setShowLoadingDots] = useState(false);
  const [showSignOut, setShowSignOut] = useState(false);
  const [showStillLoadingButton, setShowStillLoadingButton] = useState(false);
  const [disableStillLoadingButton, setDisableStillLoadingButton] =
    useState(false);
  const [alertText, setAlertText] = useState({ heading: "", subHeading: "" });

  useOnMount(() => {
    // If defined, only show loading dots after three seconds of loading
    // If not defined, show loading dots on start
    if (isLoadingPage === undefined) {
      setShowLoadingDots(true);
    }
    // Show loading dots if loading for longer than 3 seconds
    const timer = setTimeout(() => {
      if (isLoadingPage) {
        setShowLoadingDots(true);
      }
    }, 3000);
    return () => clearTimeout(timer);
  });

  useEffect(() => {
    const signOutTimer = setTimeout(() => {
      setShowSignOut(true);
    }, 20000);
    return () => clearTimeout(signOutTimer);
  }, []);

  useEffect(() => {
    if (loadingEventId) {
      const timer = setTimeout(() => {
        setShowStillLoadingButton(true);
      }, 15000); // 15 seconds
      return () => clearTimeout(timer);
    }
  }, [loadingEventId]);

  const reloadLoadingPage = useCallback(async () => {
    setDisableStillLoadingButton(true);
    const totalElapsed = loadingStartTime
      ? `${((Date.now() - loadingStartTime) / 1000).toFixed(1)}s`
      : "unknown";
    const env = getClientDiagnostics();
    await API.monitor.triggerSlackAlert({
      channel: "#app-alerts",
      title: "Event Page Long Loading",
      message: [
        `url: ${window.location.href}`,
        `eventId: ${loadingEventId}`,
        `phoneNumber: ${phoneNumber}`,
        `platform: ${isDesktop ? "desktop" : "mobile"}`,
        `mobile device: ${
          isDesktop ? "N/A" : isAndroidBrowser() ? "Android" : "iOS"
        }`,
        `loading_phase: ${loadingPhase ?? "unknown"} (total: ${totalElapsed})`,
        `in_app_browser: ${env.inAppBrowser}`,
        `network: ${env.network}`,
        `phase_timings: ${getDiagnostics ? getDiagnostics() : "n/a"}`,
        `user_agent: ${env.ua}`,
      ].join("\n"),
    });
    window.location.reload();
    setDisableStillLoadingButton(false);
  }, [
    loadingEventId,
    loadingPhase,
    loadingStartTime,
    phoneNumber,
    getDiagnostics,
  ]);

  const logout = useCallback(() => {
    try {
      mixpanel.track("Webapp: User Logout", {
        distinct_id: uid,
      });
      dispatch(logoutAllUsers());
      navigate("/");
    } catch (e: any) {
      setAlertText({
        heading: "There was an error logging out.",
        subHeading: "Please try again.",
      });
      console.log(e.message);
    }
  }, [uid, dispatch, mixpanel, navigate]);

  return error || underMaintenance ? (
    <div className="ErrorOrMaintenanceContainer">
      <div style={{ backgroundColor: Colors.GRAY6, minHeight: "100vh" }}>
        {inCreatorMode && isDesktop ? <CreatorModeTopHeader /> : <TopHeader />}
        <div style={{ display: "grid", placeItems: "center", height: "80vh" }}>
          <div
            style={{
              display: "grid",
              placeItems: "center",
              textAlign: "center",
              maxWidth: "600px",
              padding: "0 20px",
            }}
          >
            <div style={{ marginBottom: "24px" }}>
              <img
                className="LoadingPic"
                alt="Markit Logo"
                src={markitIcon}
                style={{ width: "120px", height: "auto" }}
              />
            </div>
            {error ? (
              <>
                <div
                  style={{
                    fontSize: "72px",
                    fontWeight: 700,
                    color: Colors.BLACK,
                    lineHeight: 1,
                    marginBottom: "16px",
                    opacity: 0.9,
                    letterSpacing: "-2px",
                  }}
                >
                  404
                </div>
                <div
                  style={{
                    fontSize: "32px",
                    fontWeight: 600,
                    marginBottom: "16px",
                    color: Colors.BLACK,
                  }}
                >
                  Page Not Found
                </div>
                <div
                  style={{
                    fontSize: "18px",
                    color: Colors.GRAY1,
                    marginBottom: "32px",
                    lineHeight: 1.5,
                  }}
                >
                  Oops! The page you&apos;re looking for doesn&apos;t exist or
                  has been moved. Try refreshing this page, or get back on track
                  with one of the options below.
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "16px",
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  <RectangleButton
                    buttonLabel="Go Home"
                    onPress={() => navigate("/")}
                    altTextColor={Colors.WHITE}
                    altColor={Colors.BLACK}
                    altPaddingVert={14}
                    altBorderRadius={50}
                    containerStyles={{
                      width: "fit-content",
                      paddingInline: 24,
                    }}
                  />
                  <RectangleButton
                    buttonLabel="Explore Creators"
                    onPress={() => navigate(NavigationId.EXPLORE)}
                    altTextColor={Colors.BLACK}
                    altColor={Colors.WHITE}
                    altPaddingVert={14}
                    altBorderRadius={50}
                    containerStyles={{
                      width: "fit-content",
                      paddingInline: 24,
                      border: `1px solid ${Colors.BLACK}`,
                    }}
                  />
                </div>
              </>
            ) : (
              underMaintenance && (
                <>
                  <div
                    style={{
                      fontSize: "32px",
                      fontWeight: 600,
                      marginBottom: "16px",
                      color: Colors.BLACK,
                    }}
                  >
                    We&apos;re Making Things Better
                  </div>
                  <div
                    style={{
                      fontSize: "18px",
                      color: Colors.GRAY1,
                      marginBottom: "32px",
                      lineHeight: 1.5,
                    }}
                  >
                    This page is currently under maintenance and will be back by
                    5pm EST. We&apos;re working hard to improve your experience.
                  </div>
                  <RectangleButton
                    buttonLabel="Go Home"
                    onPress={() => navigate("/home")}
                    altTextColor={Colors.WHITE}
                    altColor={Colors.BLACK}
                    altPaddingVert={14}
                    altBorderRadius={50}
                    containerStyles={{
                      width: "fit-content",
                      paddingInline: 24,
                    }}
                  />
                </>
              )
            )}
          </div>
        </div>
      </div>
      <Footer forceLight />
    </div>
  ) : (
    <div className="ErrorOrMaintenanceContainer">
      {!showLoadingDots ? (
        <div style={{ backgroundColor: Colors.GRAY6, height: "100vh" }} />
      ) : (
        <>
          <div style={{ backgroundColor: Colors.GRAY6, height: "100vh" }}>
            {inCreatorMode && isDesktop ? (
              <CreatorModeTopHeader />
            ) : (
              <TopHeader />
            )}
            <div className="LoadingScreen AlignedColumn">
              <DotWave size={47} speed={1} color={Colors.BLACK} />
              {/** Temp button to reload page if event is loading for long time, to trigger slack alert */}
              {loadingEventId && showStillLoadingButton ? (
                <div
                  className="AlignedColumn"
                  style={{ marginTop: "40px", paddingInline: 24 }}
                >
                  <span
                    className="bodySubtext"
                    style={{
                      marginBottom: "16px",
                      maxWidth: "400px",
                      textAlign: "center",
                    }}
                  >
                    Still loading? Reload the page.
                  </span>
                  <RectangleButton
                    buttonLabel="Reload"
                    onPress={reloadLoadingPage}
                    disabled={disableStillLoadingButton}
                    altTextColor={Colors.WHITE}
                    altColor={Colors.BLACK}
                    altPaddingVert={14}
                    altBorderRadius={50}
                    containerStyles={{
                      width: "fit-content",
                      paddingInline: 24,
                    }}
                  />
                </div>
              ) : showSignOut ? (
                <div
                  className="AlignedColumn"
                  style={{ marginTop: "40px", paddingInline: 24 }}
                >
                  <span
                    className="bodySubtext"
                    style={{
                      marginBottom: "16px",
                      maxWidth: "400px",
                      textAlign: "center",
                    }}
                  >
                    If the page is taking too long to load, please sign out and
                    log back in. This will refresh your session.
                  </span>
                  <RectangleButton
                    buttonLabel="Sign Out"
                    onPress={logout}
                    altTextColor={Colors.WHITE}
                    altColor={Colors.BLACK}
                    altPaddingVert={14}
                    altBorderRadius={50}
                    containerStyles={{
                      width: "fit-content",
                      paddingInline: 24,
                    }}
                  />
                </div>
              ) : null}
            </div>
          </div>
          <Footer forceLight />
        </>
      )}
      <AlertContainer
        headerComp={alertText.heading}
        subHeaderComp={
          alertText.subHeading !== "" ? alertText.subHeading : undefined
        }
        closeModal={() => setAlertText({ heading: "", subHeading: "" })}
        hideModal={alertText.heading === "" && alertText.subHeading === ""}
      />
    </div>
  );
});

export default LoadingScreen;
